import { Property } from "@/features/survey/types";

/**
 * Format a full address string from individual address components
 *
 * @param street - Street address (address1)
 * @param city - City name
 * @param state - State or province
 * @param zip - Postal/ZIP code
 * @returns Formatted address string or empty string if no components provided
 */
export function getFullAddressStr(
  street: string,
  city: string,
  state: string,
  zip: string,
): string {
  if (!street && !city && !state && !zip) return "";

  const line1 = street;
  let line2 = "";
  if (line1 && city) line2 += ", ";
  if (city) line2 += city;
  if (city && state) line2 += ", ";
  if (!city && state && zip) line2 += ", ";
  if (city && !state && zip) line2 += ", ";
  if (state) line2 += state;
  if ((city && zip) || (state && zip)) line2 += " ";
  if (zip) line2 += zip;

  const fullAddressValidParts = [line1, line2].filter((p) => p);
  return fullAddressValidParts.join("");
}

/**
 * Get the display number for a property, starting from 1 and skipping current location properties
 *
 * @param property - The property to get the display number for
 * @param properties - Array of all properties to calculate numbering from
 * @returns The display number (1-based) or undefined if property is current location
 */
export function getPropertyDisplayNumber(
  property: Property,
  properties: Property[],
): number | undefined {
  // If this is a current location property, return undefined (no number should be shown)
  if (property.isCurrentLocation) {
    return undefined;
  }

  // Filter out current location properties and hidden properties for numbering
  const numberableProperties = properties.filter(
    (p) => !p.isCurrentLocation && !p.isHidden,
  );

  // Sort properties by their original order field if it exists, otherwise by ID for consistency
  const sortedProperties = numberableProperties.sort((a, b) => {
    // If both have order values, use those
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    // If only one has order, that one comes first
    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;
    // If neither has order, sort by ID for consistency
    return a.id.localeCompare(b.id);
  });

  // Find the index of the current property in the sorted list
  const index = sortedProperties.findIndex((p) => p.id === property.id);

  // Return 1-based index, or undefined if property not found
  return index >= 0 ? index + 1 : undefined;
}

/**
 * Get the display content for a property marker (star for current location, number for others)
 *
 * @param property - The property to get display content for
 * @param properties - Array of all properties to calculate numbering from
 * @returns The display content (star symbol or number as string)
 */
export function getPropertyMarkerContent(
  property: Property,
  properties: Property[],
): string {
  if (property.isCurrentLocation) {
    return "starIcon";
  }

  const displayNumber = getPropertyDisplayNumber(property, properties);
  return displayNumber !== undefined ? displayNumber.toString() : "";
}

/**
 * Get the index of a property in the ordered list, excluding current location properties
 *
 * @param propertyId - The ID of the property to find
 * @param propertyList - Array of all properties
 * @returns The index (0-based) of the property in the filtered and sorted list, or -1 if not found
 */
export function getPropertyOrderWithoutCurrentLocation(
  propertyId: string,
  propertyList: Property[],
): number {
  if (!propertyId || !propertyList) {
    return -1;
  }

  return propertyList
    .filter((item) => !item.isCurrentLocation)
    .sort((first, second) => (first.order || 0) - (second.order || 0))
    .findIndex((item) => item.id === propertyId);
}

// Dynamic function to get property value by field key
export const getPropertyValue = (
  fieldKey: string,
  fieldId: string,
  property: Property,
): string | number | null => {
  // Access property value dynamically using bracket notation
  const value = (property as unknown as Record<string, unknown>)[fieldKey];
  if (fieldKey && value) {
    return value as string | number;
  } else {
    const { customFields } = property;
    const customField = customFields?.find(
      (eachCustom) =>
        eachCustom.fieldKey === fieldKey ||
        eachCustom.customField.customFieldId === fieldId,
    );
    return customField?.value || "";
  }
};

export const isInvalidCoordinate = (lat: string, lng: string): boolean => {
  const latNum = parseFloat(lat);
  const lngNum = parseFloat(lng);

  const isLatInvalid =
    lat.trim() === "" ||
    isNaN(latNum) ||
    latNum < -90 ||
    latNum > 90 ||
    latNum === 0;

  const isLngInvalid =
    lng.trim() === "" ||
    isNaN(lngNum) ||
    lngNum < -180 ||
    lngNum > 180 ||
    lngNum === 0;

  return isLatInvalid || isLngInvalid;
};
